import { Button, TooltipTrigger } from '@toggle/design-system';
import clsx from 'clsx';
import React, { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatInput } from '~/components/chat';
import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { TrackingMetadata } from '~/services/tracking/posthog/constants';
import { useChatNavigation } from '~/views/alfred/use-chat-navigation/useChatNavigation';

import styles from './AlfredFooter.module.scss';

export const ALFRED_FOOTER_SPACER_HEIGHT_PX = 188;

export interface AlfredFooterProps {
  renderChatInput?: (override?: { cta?: ReactNode }) => ReactNode;
  onReload?: () => void;
  selectedBadges?: ReactNode[];
  ctaLabel?: string;
  onCtaClick?: () => void;
  isCtaLoading?: boolean;
  suggestedQuestion?: string;
  isLoading?: boolean;
  submitCallback?: (message?: string) => void;
  isActive?: boolean;
  emptyState?: boolean;
  shouldHide?: boolean;
  showReloadButton?: boolean;
  trackingMetadata?: TrackingMetadata;
  fixed?: boolean;
}

export const AlfredFooter = ({
  onReload,
  suggestedQuestion,
  isLoading = false,
  submitCallback,
  isActive,
  emptyState = false,
  shouldHide = false,
  showReloadButton = false,
  trackingMetadata,
  fixed = false,
}: AlfredFooterProps) => {
  const { mode, setMode, resetPreviousMode } = useChatNavigation();
  const { t } = useTranslation(['chat']);

  useEffect(() => {
    if (mode !== 'research') {
      setMode('research');
    }

    resetPreviousMode();
  }, []);

  return (
    <footer
      className={clsx(styles.alfredFooterRoot, {
        [styles.hidden]: shouldHide,
        [styles.fixed]: fixed,
        [styles.sticky]: !fixed,
      })}
    >
      <div
        className={clsx(styles.leftControls, {
          [styles.leftControlsHidden]: emptyState || !showReloadButton,
        })}
      >
        <TooltipContainer
          label={t('chat:reload')}
          trigger={[TooltipTrigger.Hover]}
        >
          <Button
            variant="empty"
            size="small"
            iconName="ArrowRotateRight"
            aria-label={t('chat:reload')}
            onClick={!isLoading ? onReload : undefined}
            isLoading={isLoading && isActive}
          />
        </TooltipContainer>
      </div>

      <div className={styles.inputRegion}>
        <ChatInput
          config={{
            autoFocus: false,
            alfredEverywhere: true,
            suggestedValue: suggestedQuestion,
            allowedModes: ['document', 'research'],
          }}
          submitCallback={submitCallback}
          trackingMetadata={trackingMetadata}
        />
      </div>
    </footer>
  );
};
